import firebase from 'firebase';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useConfig } from '../../hooks/useConfig';
import useScript from '../../hooks/useScript';
import {
  InterfaceConfigOptions,
  JitsiMeetAPI,
  JitsiMeetAPIOptions,
} from '../../types/jitsiTypes';
import { isInDevelopment } from '../../utils/detectEnv';

type TokenData = {
  jwt: string;
  user: firebase.User;
  isAdmin: boolean;
  cookie: string;
};

const useTokenData = () => {
  const [token, setToken] = useState<TokenData | null>(null);
  useEffect(() => {
    firebase
      .app()
      .functions(isInDevelopment ? undefined : 'europe-west3')
      .httpsCallable('jitsi')()
      .then((result) => {
        setToken(result.data);
      })
      .catch(async (e) => {
        console.log(e);
        await firebase
          .firestore()
          .collection('errors')
          .add({ id: 'jitsiCF', error: JSON.stringify(e) });
      });
  }, []);

  return token;
};

export const useJitsi = (
  roomId: string,
  ref: HTMLDivElement | null,
  roomTitle: string
) => {
  const [loading, setLoading] = useState(true);
  const [hasJitsi, setHasJitsi] = useState(false);
  const [error, setError] = useState('');
  const [jitsi, setJitsi] = useState<JitsiMeetAPI | null>(null);
  const scriptLoaded = useScript('https://8x8.vc/external_api.js');
  const tokenData = useTokenData();
  const history = useHistory();
  const firebaseConfig = useConfig();

  useEffect(() => {
    if (!ref) {
      setError('no-ref');
      return;
    }

    if (!tokenData) {
      setError('no-token-data');
      return;
    }

    if (!window.JitsiMeetExternalAPI || !scriptLoaded) {
      setError(
        'JitsiMeetExternalAPI is not available, check if external_api.js was loaded'
      );
      return;
    }

    if (!firebaseConfig) {
      setError('no-firebase-config');
      return;
    }

    const TOOLBAR_BUTTONS: InterfaceConfigOptions['TOOLBAR_BUTTONS'] =
      tokenData.isAdmin
        ? [
            'microphone',
            'videoquality',
            'settings',
            'hangup',
            'camera',
            'fullscreen',
            'chat',
            'feedback',
            'sharedvideo',
            'desktop',
            'shortcuts',
          ]
        : [
            'microphone',
            'videoquality',
            'settings',
            'hangup',
            'camera',
            'fullscreen',
            'chat',
            'feedback',
            'shortcuts',
          ];

    const options: JitsiMeetAPIOptions = {
      parentNode: ref,
      roomName: `${tokenData.cookie}/${roomId}`,
      jwt: tokenData.jwt,
      width: '100%',
      height: '100%',
      interfaceConfigOverwrite: {
        TOOLBAR_BUTTONS,
        DISABLE_DOMINANT_SPEAKER_INDICATOR: true,
        DISABLE_FOCUS_INDICATOR: true,
        SHOW_CHROME_EXTENSION_BANNER: false,
        DISABLE_RINGING: true,
        DISABLE_TRANSCRIPTION_SUBTITLES: true,
        DISABLE_VIDEO_BACKGROUND: true,
        VIDEO_LAYOUT_FIT: 'both',
        MAXIMUM_ZOOMING_COEFFICIENT: 1,
      },

      configOverwrite: {
        startWithAudioMuted: !tokenData.isAdmin,
        disableTileView: true,
        enableWelcomePage: false,
        defaultLanguage: 'de',
        disableNS: tokenData.isAdmin,
        disableHPF: tokenData.isAdmin,
      },
    };

    if (!hasJitsi) {
      const client = new window.JitsiMeetExternalAPI('8x8.vc', { ...options });
      client.executeCommand('subject', roomTitle);
      client.addEventListener('readyToClose', () => {
        history.push('/');
      });
      setJitsi(client);
      setHasJitsi(true);
      setLoading(false);
      setError('');
    }
  }, [
    window.JitsiMeetExternalAPI,
    tokenData,
    scriptLoaded,
    ref,
    firebaseConfig,
  ]);

  return { jitsi, error, loading };
};
